import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/hooks/useConfig";
import { labelFor, sentenceLabel } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Pencil, CheckCircle, XCircle, Download, FileText, MapPin, CreditCard, User, Undo2, Send, Clock } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/dateFormat";

const STATUS_COLORS = {
  applied: "bg-blue-50 text-blue-700 border-blue-200",
  kyc_submitted: "bg-amber-50 text-amber-700 border-amber-200",
  under_review: "bg-violet-50 text-violet-700 border-violet-200",
  sent_back: "bg-orange-50 text-orange-700 border-orange-300",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-600 border-red-200",
};

const REQ_STATUS_LABEL = { pending: "Waiting", approved: "Approved", rejected: "Rejected" };

export default function DPDetailPage() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getValues } = useConfig();
  const rejectionReasons = getValues("dp_rejection_reasons");
  const sendBackReasons = getValues("dp_send_back_reasons");
  const changeReasons = getValues("dp_change_reasons");
  const [profile, setProfile] = useState(null);
  const [party, setParty] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showSendBack, setShowSendBack] = useState(false);
  const [sendBackReason, setSendBackReason] = useState("");
  const [sendBackNote, setSendBackNote] = useState("");
  const [brands, setBrands] = useState([]);
  const [requests, setRequests] = useState([]);
  const [showPinReq, setShowPinReq] = useState(false);
  const [pinReq, setPinReq] = useState({ pincodes: "", brands: [], reason: "" });
  const [pinErrors, setPinErrors] = useState([]);
  const [processing, setProcessing] = useState(false);

  const perms = user?.permissions || [];
  const isAdmin = (user?.roles || []).some(r => ["Admin", "admin"].includes(r)) || perms.includes("admin.full_access") || perms.includes("dp.admin");
  const canApprove = isAdmin || perms.includes("dp.partners.approve");
  const canEdit = isAdmin || perms.includes("dp.partners.edit");

  const loadRequests = () => {
    api.get(`/dp/territory-requests?status=&profile_id=${profileId}`).then(r => setRequests(r.data || [])).catch(() => {});
  };

  const load = () => {
    setLoading(true);
    api.get(`/dp/partners/${profileId}`)
      .then(r => {
        setProfile(r.data);
        setParty(r.data.party || null);
        if (r.data.party_id) {
          api.get(`/crm/activities/${r.data.party_id}`).then(a => setActivities(a.data || [])).catch(() => {});
        }
      })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    loadRequests();
    api.get("/dp/brands").then(r => setBrands((r.data || []).filter(b => b.status === "active"))).catch(() => {});
  }, [profileId]);

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await api.post(`/dp/partners/${profileId}/approve`);
      toast.success("Partner approved and activated");
      load();
    } catch (e) {
      const d = e.response?.data;
      if (e.response?.status === 409 && d?.detail?.conflicts) {
        const names = d.detail.conflicts.map(c => `${c.business_name} (${c.conflicting_pincodes.join(", ")})`).join("; ");
        toast.error(`Territory conflict: ${names}`);
      } else {
        toast.error(typeof d?.detail === "string" ? d.detail : "Approval failed");
      }
    } finally { setProcessing(false); }
  };

  const handleReject = async () => {
    if (!rejectReason) { toast.error("Select a reason"); return; }
    setProcessing(true);
    try {
      await api.post(`/dp/partners/${profileId}/reject`, { rejection_reason: rejectReason });
      toast.success("Partner rejected");
      setShowReject(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setProcessing(false); }
  };

  const handleSendBack = async () => {
    if (!sendBackReason) { toast.error("Select a reason"); return; }
    if (sendBackReason === "other" && !sendBackNote.trim()) { toast.error("Add a note when the reason is Other"); return; }
    setProcessing(true);
    try {
      await api.post(`/dp/partners/${profileId}/send-back`, { send_back_reason: sendBackReason, send_back_note: sendBackNote });
      toast.success("Application sent back to the agent");
      setShowSendBack(false); setSendBackReason(""); setSendBackNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send back failed"); }
    finally { setProcessing(false); }
  };

  const handleResubmit = async () => {
    setProcessing(true);
    try {
      await api.post(`/dp/partners/${profileId}/resubmit`, {});
      toast.success("Resubmitted for review");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Resubmit failed"); }
    finally { setProcessing(false); }
  };

  const handlePinRequest = async () => {
    const pins = pinReq.pincodes.split(",").map(p => p.trim()).filter(Boolean);
    const bad = pins.filter(p => !/^\d{6}$/.test(p));
    if (pins.length === 0) { setPinErrors(["Enter at least one pincode"]); return; }
    if (bad.length > 0) { setPinErrors(bad.map(p => `${p} is not a 6-digit pincode`)); return; }
    if (pinReq.brands.length === 0) { setPinErrors(["Select at least one brand"]); return; }
    setPinErrors([]);
    const additions = [];
    pins.forEach(p => pinReq.brands.forEach(b => additions.push({ pincode: p, brand_id: b })));
    setProcessing(true);
    try {
      await api.post(`/dp/partners/${profileId}/territory-request`, { change_type: "add_grants", additions, reason: pinReq.reason });
      toast.success("Request sent for approval");
      setShowPinReq(false); setPinReq({ pincodes: "", brands: [], reason: "" });
      loadRequests();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Request failed";
      setPinErrors(msg.split("; "));
      toast.error(msg);
    } finally { setProcessing(false); }
  };

  if (loading) return <div className="p-6 text-xs text-zinc-400">Loading...</div>;
  if (!profile) return <div className="p-6 text-xs text-zinc-400">Profile not found.</div>;

  const actionable = ["applied", "kyc_submitted", "under_review"].includes(profile.onboarding_status);
  const sentBack = profile.onboarding_status === "sent_back";
  const isActiveDp = ["approved", "active"].includes(profile.onboarding_status);
  const history = profile.send_back_history || [];

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="dp-detail-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dp")}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">{profile.business_name}</h1>
            <p className="text-xs text-zinc-400">{profile.contact_person} · {profile.phone}</p>
          </div>
          <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_COLORS[profile.onboarding_status] || ""}`} data-testid="status-badge">
            {profile.onboarding_status?.replace(/_/g, " ")}
          </Badge>
        </div>
        <div className="flex gap-2">
          {canEdit && <Button variant="outline" size="sm" onClick={() => navigate(`/dp/apply?edit=${profileId}`)} data-testid="edit-btn"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>}
          {canEdit && sentBack && (
            <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={handleResubmit} disabled={processing} data-testid="resubmit-btn">
              <Send className="w-3.5 h-3.5 mr-1" /> Resubmit
            </Button>
          )}
          {canEdit && isActiveDp && (
            <Button size="sm" variant="outline" onClick={() => setShowPinReq(v => !v)} data-testid="request-pincodes-btn">
              <MapPin className="w-3.5 h-3.5 mr-1" /> Request More Pincodes
            </Button>
          )}
          {canApprove && actionable && (
            <>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleApprove} disabled={processing} data-testid="approve-btn">
                <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="text-orange-700 border-orange-300" onClick={() => setShowSendBack(true)} data-testid="send-back-btn">
                <Undo2 className="w-3.5 h-3.5 mr-1" /> Send Back
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => setShowReject(true)} data-testid="reject-btn">
                <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
              </Button>
            </>
          )}
        </div>
      </div>

      {sentBack && (
        <div className="mb-4 p-4 bg-orange-50 border border-orange-300 rounded-sm" data-testid="sent-back-box">
          <div className="flex items-center gap-2 mb-1">
            <Undo2 className="w-4 h-4 text-orange-600" />
            <h3 className="text-xs font-semibold text-orange-800 uppercase tracking-wider">Sent back for correction</h3>
          </div>
          <p className="text-sm text-orange-900" data-testid="sent-back-reason">{sentenceLabel(profile.send_back_reason)}</p>
          {profile.send_back_note && <p className="text-xs text-orange-800 mt-1" data-testid="sent-back-note">{profile.send_back_note}</p>}
          <p className="text-[11px] text-orange-700 mt-2" data-testid="sent-back-by">
            By {profile.send_back_by_name || "reviewer"} on {profile.send_back_at ? fmtDateTime(profile.send_back_at) : "—"}
          </p>
          <p className="text-[11px] text-orange-700 mt-1">Fix the application and click Resubmit to send it back for review.</p>
        </div>
      )}

      {showSendBack && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-300 rounded-sm" data-testid="send-back-form">
          <h3 className="text-xs font-semibold text-orange-800 mb-2">Send Back</h3>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs text-orange-800 font-medium">Reason</Label>
              <Select value={sendBackReason} onValueChange={setSendBackReason}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-send-back-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>{sendBackReasons.map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={handleSendBack} disabled={processing} data-testid="confirm-send-back">Confirm Send Back</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowSendBack(false)}>Cancel</Button>
          </div>
          <div className="mt-2">
            <Label className="text-xs text-orange-800 font-medium">Note{sendBackReason === "other" ? " (required)" : " (optional)"}</Label>
            <Textarea rows={2} value={sendBackNote} onChange={e => setSendBackNote(e.target.value)} className="text-xs mt-1" placeholder="What exactly should the agent fix?" data-testid="input-send-back-note" />
          </div>
        </div>
      )}

      {showPinReq && (
        <div className="mb-4 p-4 bg-zinc-50 border border-zinc-300 rounded-sm" data-testid="pincode-request-form">
          <h3 className="text-xs font-semibold text-zinc-700 uppercase tracking-wider mb-3">Request More Pincodes</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Pincodes (comma-separated)</Label>
              <Input value={pinReq.pincodes} onChange={e => setPinReq(f => ({ ...f, pincodes: e.target.value }))} className="h-8 text-xs mt-1" placeholder="500032, 500081" data-testid="input-request-pincodes" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Reason</Label>
              <Select value={pinReq.reason} onValueChange={v => setPinReq(f => ({ ...f, reason: v }))}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-request-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>{changeReasons.map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs text-zinc-500">Brands (tick at least one)</Label>
            <div className="flex flex-wrap gap-3 mt-1">
              {brands.map(b => (
                <label key={b._id} className="flex items-center gap-1 text-xs text-zinc-600">
                  <input type="checkbox" checked={pinReq.brands.includes(b._id)} className="rounded border-zinc-300" data-testid={`request-brand-${b._id}`}
                    onChange={e => setPinReq(f => ({ ...f, brands: e.target.checked ? [...f.brands, b._id] : f.brands.filter(x => x !== b._id) }))} />
                  {b.display_name || b.name}
                </label>
              ))}
            </div>
          </div>
          {pinErrors.length > 0 && (
            <ul className="mt-3 text-[11px] text-red-600 list-disc pl-4 space-y-0.5" data-testid="pincode-request-errors">
              {pinErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
          <div className="flex justify-end gap-2 mt-3">
            <Button size="sm" variant="ghost" onClick={() => setShowPinReq(false)}>Cancel</Button>
            <Button size="sm" onClick={handlePinRequest} disabled={processing} data-testid="submit-pincode-request">Send Request</Button>
          </div>
        </div>
      )}

      {requests.length > 0 && (
        <div className="mb-4 border border-zinc-200 rounded-sm p-4" data-testid="pending-requests-card">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-zinc-400" />
            <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Pending requests</h3>
          </div>
          <div className="space-y-2">
            {requests.map(r => (
              <div key={r._id} className="flex items-start justify-between text-xs border-b border-zinc-100 pb-2 last:border-0 last:pb-0" data-testid={`request-${r._id}`}>
                <div>
                  <span className="text-zinc-800 font-medium capitalize">{(r.change_type || "").replace(/_/g, " ")}</span>
                  <span className="text-zinc-500 ml-2">{(r.additions || []).map(a => a.pincode).filter((v, i, a2) => a2.indexOf(v) === i).join(", ") || (r.removals || []).map(a => a.pincode).join(", ")}</span>
                  {r.reason && <p className="text-[10px] text-zinc-400 mt-0.5">{sentenceLabel(r.reason)}</p>}
                  {r.status === "rejected" && r.reject_note && <p className="text-[10px] text-red-500 mt-0.5">Rejected: {r.reject_note}</p>}
                </div>
                <Badge variant="outline" className={`text-[10px] ${r.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" : r.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-600 border-red-200"}`} data-testid={`request-status-${r._id}`}>
                  {REQ_STATUS_LABEL[r.status] || r.status}
                </Badge>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-zinc-400 mt-2 italic">Approval happens in Territory Approvals.</p>
        </div>
      )}

      {profile.possible_duplicate && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800" data-testid="dup-banner">
          This application may be a duplicate of an existing Direct Partner.
        </div>
      )}

      {showReject && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm flex items-end gap-2" data-testid="reject-form">
          <div className="flex-1">
            <label className="text-xs text-red-700 font-medium" data-testid="reject-form-title">Reject (final)</label>
            <Select value={rejectReason} onValueChange={setRejectReason}>
              <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-reject-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
              <SelectContent>{rejectionReasons.map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[10px] text-red-600 mt-1">Final decision — the agent cannot resubmit after this.</p>
          </div>
          <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={handleReject} disabled={processing} data-testid="confirm-reject">Confirm Reject</Button>
          <Button size="sm" variant="ghost" onClick={() => setShowReject(false)}>Cancel</Button>
        </div>
      )}

      <div className="flex gap-1 border-b border-zinc-200 mb-4">
        {["details", "documents", "timeline"].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 text-xs font-medium border-b-2 capitalize ${tab === t ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400"}`} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      {tab === "details" && (
        <div className="grid grid-cols-2 gap-4">
          <InfoCard icon={<User className="w-4 h-4" />} title="Business">
            <Row label="Legal Name" value={profile.legal_name} />
            <Row label="Business Name" value={profile.business_name} />
            <Row label="Contact" value={profile.contact_person} />
            <Row label="Phone" value={profile.phone} />
            <Row label="Email" value={profile.email} />
            <Row label="Address" value={[profile.address, profile.city, profile.state].filter(Boolean).join(", ")} />
          </InfoCard>
          <InfoCard icon={<FileText className="w-4 h-4" />} title="Tax & Bank">
            <Row label="GST" value={profile.gst_number} mono />
            <Row label="PAN" value={profile.pan_number} mono />
            <Row label="Bank A/c" value={profile.bank_account_number} mono />
            <Row label="IFSC" value={profile.bank_ifsc} mono />
            <Row label="Bank" value={profile.bank_name} />
          </InfoCard>
          <InfoCard icon={<MapPin className="w-4 h-4" />} title="Territory">
            <Row label="Zone" value={profile.territory_zone} />
            <Row label="CRM Stage" value={profile.party_stage || party?.current_stage_key || "—"} />
            <div className="mt-2">
              <p className="text-[10px] text-zinc-400 mb-1 font-medium">Territory ({(profile.territory_grants || []).length})</p>
              {(profile.territory_grants || []).length === 0 ? <p className="text-[10px] text-zinc-400">No grants</p> : (
                <div className="space-y-0.5 max-h-32 overflow-y-auto">
                  {(profile.territory_grants || []).map((g, i) => (
                    <div key={i} className="flex justify-between text-[10px]">
                      <span className="font-mono text-zinc-600">{g.pincode}</span>
                      <span className="text-zinc-500">{g.brand_name || g.brand_id?.slice(-6)}{g.brand_status && g.brand_status !== "live" ? <span className="text-red-500 ml-1" data-testid={`grant-brand-status-${i}`}>({g.brand_status === "suspended" ? "suspended" : "not live"})</span> : null}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </InfoCard>
          <InfoCard icon={<CreditCard className="w-4 h-4" />} title="OMS Customer">
            {profile.oms_customer_id ? (
              <>
                <Row label="Customer ID" value={profile.oms_customer_id} mono />
                <Row label="Linked" value={fmtDate(profile.oms_customer_linked_at)} />
                <p className="text-[10px] text-zinc-400 mt-2 italic">Orders, advances and credit notes for this partner are managed in OMS as a normal customer.</p>
                <Button size="sm" variant="outline" className="h-6 text-[10px] mt-1" onClick={() => window.open(`/oms/customers`, "_blank")} data-testid="view-oms-customer">
                  View in OMS →
                </Button>
              </>
            ) : (
              <p className="text-xs text-zinc-400">Not linked yet — link happens on approval.</p>
            )}
          </InfoCard>
        </div>
      )}

      {tab === "documents" && (
        <div data-testid="documents-section">
          {(profile.kyc_documents || []).length === 0 ? (
            <p className="text-center text-zinc-400 text-xs py-8 border border-dashed border-zinc-200 rounded-sm">No KYC documents uploaded yet.</p>
          ) : (
            <div className="space-y-2">
              {profile.kyc_documents.map(d => (
                <div key={d.doc_id} className="flex items-center justify-between border border-zinc-200 rounded-sm px-3 py-2" data-testid={`doc-${d.doc_id}`}>
                  <div>
                    <span className="text-xs font-medium text-zinc-800">{labelFor(d.doc_type)}</span>
                    <span className="text-xs text-zinc-400 ml-2">{d.filename}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-zinc-400">{fmtDate(d.uploaded_at)}</span>
                    <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/dp/partners/${profileId}/documents/${d.doc_id}`, "_blank")} data-testid={`download-${d.doc_id}`}>
                      <Download className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "timeline" && (
        <div data-testid="timeline-section">
          {history.length > 0 && (
            <div className="mb-4 border border-orange-200 rounded-sm p-3" data-testid="send-back-history">
              <h3 className="text-[10px] font-semibold text-orange-700 uppercase tracking-wider mb-2">Send back &amp; resubmit history</h3>
              <div className="space-y-1.5">
                {history.map((h, i) => (
                  <div key={i} className="flex items-start justify-between text-xs" data-testid={`history-${i}`}>
                    <div>
                      <span className="font-medium text-zinc-800">{h.action === "sent_back" ? "Sent back" : "Resubmitted"}</span>
                      {h.reason && <span className="text-zinc-500 ml-2" data-testid={`history-reason-${i}`}>{sentenceLabel(h.reason)}</span>}
                      {h.note && <p className="text-[10px] text-zinc-500">{h.note}</p>}
                    </div>
                    <span className="text-[10px] text-zinc-400 whitespace-nowrap ml-3">{h.by_name} · {fmtDateTime(h.at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {activities.length === 0 ? (
            <p className="text-center text-zinc-400 text-xs py-8 border border-dashed border-zinc-200 rounded-sm">No activity yet.</p>
          ) : (
            <div className="space-y-2">
              {activities.map((a, i) => (
                <div key={a._id || i} className="border-l-2 border-zinc-200 pl-3 py-1">
                  <p className="text-xs text-zinc-800">{a.summary || a.type}</p>
                  <p className="text-[10px] text-zinc-400">{a.outcome && <span className="capitalize">{a.outcome} · </span>}{fmtDateTime(a.occurred_at || a.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoCard({ icon, title, children }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-zinc-400">{icon}</span>
        <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">{title}</h3>
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-zinc-400">{label}</span>
      <span className={`text-zinc-700 text-right ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}
